import { Check, X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import type { Contract } from '@/types/api/contract';
import getProductIcon from '@/lib/product-icon';
import { getLastContractId, setLastContractId } from '@/lib/last-selected-contract';

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getStatusVariant = (statutCode?: string) => {
    switch (statutCode) {
        case '1':
            return 'success';
        case '6':
        case '5':
            return 'destructive';
        case 'I':
        case '2':
            return 'warning';
        default:
            return 'secondary';
    }
};

export interface ContractPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (contract: Contract) => void;
    contracts: Contract[];
    title?: string;
    /** Contrat mis en avant ; à défaut, le dernier choisi par l'utilisateur. */
    activeContractId?: string | number | null;
}

const ContractPickerModal = ({ isOpen, onClose, onSelect, contracts, title = 'Choisissez votre contrat', activeContractId }: ContractPickerModalProps) => {
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const lastFocusedRef = useRef<Element | null>(null);
    /* Sans sélection explicite du parent, on met en avant le dernier contrat choisi */
    const effectiveActiveId = activeContractId ?? getLastContractId();

    useEffect(() => {
        if (!isOpen) return;

        lastFocusedRef.current = document.activeElement;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        const focusTimer = setTimeout(() => {
            if (dialogRef.current) {
                const firstFocusable = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
                if (firstFocusable) firstFocusable.focus();
            }
        }, 50);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            clearTimeout(focusTimer);
            const lastFocused = lastFocusedRef.current as HTMLElement | null;
            if (lastFocused && typeof lastFocused.focus === 'function') {
                lastFocused.focus();
            }
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="bg-card rounded-xl shadow-lg w-full max-w-lg max-h-[80vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-6 pb-6 border-b">
                    <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Fermer la fenêtre"
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>

                <div className="p-6 space-y-3">
                    {contracts.map((c) => {
                        const ProductIcon = getProductIcon(c.vehicule?.codeProduit);
                        const isActive = String(effectiveActiveId) === String(c.id);

                        return (
                            <button
                                key={c.id}
                                type="button"
                                aria-pressed={isActive}
                                onClick={() => {
                                    setLastContractId(c.id);
                                    onSelect(c);
                                    onClose();
                                }}
                                className={`w-full flex flex-wrap items-start gap-3 p-6 rounded-lg border text-left cursor-pointer transition-all duration-200
 ${isActive
                                        ? 'border-primary bg-primary/5 shadow-md'
                                        : 'border-border bg-card hover:border-primary/30 hover:bg-primary/5 shadow-sm'
                                    }
                                `}
                            >
                                <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${isActive ? 'bg-primary text-white' : 'bg-muted/50 text-muted-foreground'}`}>
                                    <ProductIcon className="w-5 h-5" aria-hidden="true" />
                                </div>
                                <div className="flex-1 min-w-[140px]">
                                    <p className="font-semibold text-sm break-words">
                                        Contrat N°{c.numeroPolice}
                                    </p>
                                    {c.vehicule?.marque && (
                                        <p className="text-sm text-muted-foreground break-words">
                                            {c.vehicule.marque}{c.vehicule?.immatriculation ? ` - ${c.vehicule.immatriculation}` : ''}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-auto">
                                    {c.situation?.statut && (
                                        <Badge variant={getStatusVariant(c.situation.statutCode)} className="text-[10px] sm:text-sm">
                                            {c.situation.statut}
                                        </Badge>
                                    )}
                                    {isActive && (
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white">
                                            <Check className="w-4 h-4" aria-hidden="true" />
                                            <span className="sr-only">Contrat actuellement sélectionné</span>
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ContractPickerModal;
